/**
 * Teams API client (pirx-z0v4g.7).
 *
 * Thin wrappers over the `/api/teams/*` routes backing the team-settings surface
 * and the invite-accept page. Authorization is enforced server-side (member/
 * owner/admin gates in the team controller); these wrappers just carry the call.
 *
 * Date-typed columns arrive as ISO strings over JSON, hence `string | null`.
 */

import { request, requestVoid } from "../api/request";

export type TeamMemberRole = "owner" | "admin" | "member";
/** Roles that can be assigned via invite (owner is transfer-only). */
export type InvitableRole = "admin" | "member";

export interface TeamMember {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: TeamMemberRole;
  /** null while the invite is still pending (not yet accepted). */
  acceptedAt: string | null;
}

export interface TeamDetails {
  id: string;
  name: string;
  ownerId: string;
  createdAt: string;
  currentUserRole: TeamMemberRole;
  members: TeamMember[];
}

export interface CreateInviteResult {
  id: string;
  teamId: string;
  email: string;
  role: TeamMemberRole;
  token: string;
  expiresAt: string;
}

export interface AcceptInviteResult {
  teamId: string;
  teamName: string;
  role: TeamMemberRole;
  membershipId: string;
}

/** Team details incl. the member roster. Server 404s a non-member. */
export async function getTeam(teamId: string): Promise<TeamDetails> {
  return request<TeamDetails>(`/teams/${teamId}`);
}

/** Create an invite for `email`. Owner/admin only (server-gated). */
export async function createTeamInvite(
  teamId: string,
  input: { email: string; role: InvitableRole },
): Promise<CreateInviteResult> {
  return request<CreateInviteResult>(`/teams/${teamId}/invites`, {
    method: "POST",
    body: input,
    contentType: "json",
  });
}

/** Accept an invite by token. The caller's authenticated email must match. */
export async function acceptTeamInvite(
  token: string,
): Promise<AcceptInviteResult> {
  return request<AcceptInviteResult>("/teams/invites/accept", {
    method: "POST",
    body: { token },
    contentType: "json",
  });
}

/** Change a member's role. Owner/admin only (server-gated). */
export async function updateTeamMemberRole(
  teamId: string,
  userId: string,
  role: TeamMemberRole,
): Promise<unknown> {
  return request(`/teams/${teamId}/members/${userId}`, {
    method: "PUT",
    body: { role },
    contentType: "json",
  });
}

/** Remove a member (or leave, if removing yourself). */
export async function removeTeamMember(
  teamId: string,
  userId: string,
): Promise<void> {
  return requestVoid(`/teams/${teamId}/members/${userId}`, {
    method: "DELETE",
  });
}
